'use client'

import { useState, useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import type { NoteBreakdown as NoteBreakdownType } from '@/types/notes'
import { cn } from '@/lib/utils'
import { TapeNote } from './TapeNote'

interface NoteBreakdownProps {
  breakdown: NoteBreakdownType
  className?: string
}

export function NoteBreakdown({ breakdown, className }: NoteBreakdownProps) {
  const [open, setOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <div
      className={cn(
        'rounded-2xl shadow-sm transition-colors duration-200',
        open ? 'bg-primary-light/30' : 'bg-bg',
        className
      )}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6"
        aria-expanded={open}
      >
        <div>
          <span className="text-ink text-base font-semibold">
            {breakdown.title}
          </span>
          <span className="text-body ml-2 text-sm">{breakdown.subtitle}</span>
        </div>
        <ChevronDown
          size={18}
          className={cn(
            'text-hint shrink-0 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      <div
        className={cn(
          'grid transition-all duration-300',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div ref={contentRef} className="overflow-hidden">
          <div className="space-y-4 px-5 pb-5 md:px-6 md:pb-6">
            {breakdown.steps.map((step, index) => (
              <div key={index} className="flex gap-3">
                <span className="bg-primary-light text-primary flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-[family-name:var(--font-mono)] text-xs font-bold">
                  {index + 1}
                </span>
                <div>
                  <span className="text-ink font-[family-name:var(--font-handwritten)] text-lg font-semibold">
                    {step.title}
                  </span>
                  <p className="text-body mt-0.5 text-sm leading-relaxed">
                    {step.text}
                  </p>
                </div>
              </div>
            ))}

            <TapeNote variant="primary" rotation={-0.5}>
              {breakdown.memoryTrick}
            </TapeNote>
          </div>
        </div>
      </div>
    </div>
  )
}
